import React, { useState } from 'react';

// Dashboard-native character creation (2026-07-14). Replaces the legacy
// launch into the old select screen: a themed modal with a quick-create form
// and a hand-off into the character-sheet import review. Presentation-only —
// the container (index.js) owns the create mutation and navigation.

// Maps the dashboard theme onto the import review modal's --ci-* CSS custom
// properties so the review screen renders in the active theme instead of its
// standalone palette. Semantic status colors (success/danger pills) stay
// theme-independent by design (P9).
export const buildImportReviewThemeVars = (theme) => {
  const c = theme?.colors || {};
  const e = theme?.effects || {};
  return {
    '--ci-overlay-bg': c.bgModal || 'rgba(0,0,0,0.8)',
    '--ci-surface': c.bgCardSolid || '#141922',
    '--ci-text': c.textPrimary || '#f6f1df',
    '--ci-text-soft': c.textSecondary || '#c8beaa',
    '--ci-text-muted': c.textMuted || '#b9b0a0',
    '--ci-text-label': c.textSecondary || '#d8cfb7',
    '--ci-accent': c.accent || '#d6b14c',
    '--ci-accent-border': c.borderAccent || c.accent || 'rgba(214,177,76,0.62)',
    '--ci-accent-border-soft': c.borderPrimary || 'rgba(214,177,76,0.24)',
    '--ci-border': c.borderLight || 'rgba(255,255,255,0.2)',
    '--ci-panel-border': c.borderLight || 'rgba(255,255,255,0.14)',
    '--ci-input-bg': c.bgInput || '#0f131a',
    '--ci-btn-bg': c.bgCardHover || '#2d3c4e',
    '--ci-btn-text': c.textPrimary || '#fff7d6',
    '--ci-btn-secondary-bg': 'rgba(0,0,0,0.25)',
    '--ci-btn-disabled-bg': c.bgCardHover || '#333740',
    '--ci-danger-bg': c.dangerBg || '#5a2730',
    '--ci-danger-border': c.danger || 'rgba(220,91,91,0.74)',
    '--ci-warn-bg': c.warningBg || 'rgba(214,177,76,0.09)',
    '--ci-warn-border': c.warning || 'rgba(214,177,76,0.56)',
    '--ci-radius': e.borderRadiusLg || '12px',
    '--ci-radius-sm': e.borderRadiusSm || '4px',
  };
};

const fieldStyle = (theme) => ({
  width: '100%',
  background: 'rgba(0,0,0,0.32)',
  border: `1px solid ${theme.colors.borderLight}`,
  color: theme.colors.textPrimary,
  borderRadius: theme.effects?.borderRadiusSm || '4px',
  padding: '10px 12px',
  fontSize: '0.95rem',
  fontFamily: theme.typography?.fontFamily,
  boxSizing: 'border-box',
});

const labelStyle = (theme) => ({
  display: 'block',
  marginBottom: 6,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.78rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.colors.textMuted,
});

export const CreateCharacterModal = ({ theme, onClose, onCreate, onOpenImport }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [name, setName] = useState('');
  const [race, setRace] = useState('');
  const [charClass, setCharClass] = useState('');
  const [level, setLevel] = useState('1');
  const [maxHp, setMaxHp] = useState('25');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = !!onCreate && !!name.trim() && !!race.trim()
    && Number.isFinite(Number.parseInt(maxHp, 10)) && Number.parseInt(maxHp, 10) > 0;

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true); setError(null);
    try {
      await onCreate({
        name: name.trim(),
        race: race.trim(),
        charClass: charClass.trim() || null,
        level: Math.max(1, Math.min(20, Number.parseInt(level, 10) || 1)),
        maxHp: Math.max(1, Math.min(999, Number.parseInt(maxHp, 10))),
      });
      // Success navigates away (the dashboard unmounts); no local close needed.
    } catch (err) {
      setError(err?.message || String(err));
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="New character"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleCreate}
        style={{
          width: '100%', maxWidth: 480,
          maxHeight: '90vh', overflowY: 'auto',
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: theme.effects?.borderRadiusLg || '12px',
          padding: 24,
          boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
          color: c.textPrimary,
        }}
      >
        <h2 style={{
          margin: '0 0 4px',
          fontFamily: t.headingFamily,
          fontSize: '1.3rem',
          color: c.accent,
        }}>New Character</h2>
        <p style={{
          margin: '0 0 18px',
          color: c.textMuted,
          fontSize: '0.86rem',
          fontFamily: t.fontFamily,
        }}>Starts as a solo character on your account — add it to a campaign any time.</p>

        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={labelStyle(theme)} htmlFor="ccm-name">Name *</label>
            <input
              id="ccm-name"
              type="text"
              value={name}
              placeholder="Character name"
              onChange={(e) => setName(e.target.value)}
              autoFocus
              style={fieldStyle(theme)}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle(theme)} htmlFor="ccm-race">Race *</label>
              <input
                id="ccm-race"
                type="text"
                value={race}
                placeholder="Human, Elf…"
                onChange={(e) => setRace(e.target.value)}
                style={fieldStyle(theme)}
              />
            </div>
            <div>
              <label style={labelStyle(theme)} htmlFor="ccm-class">Class</label>
              <input
                id="ccm-class"
                type="text"
                value={charClass}
                placeholder="Rogue"
                onChange={(e) => setCharClass(e.target.value)}
                style={fieldStyle(theme)}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle(theme)} htmlFor="ccm-level">Level</label>
              <input
                id="ccm-level"
                type="number"
                min={1}
                max={20}
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                style={fieldStyle(theme)}
              />
            </div>
            <div>
              <label style={labelStyle(theme)} htmlFor="ccm-maxhp">Max HP *</label>
              <input
                id="ccm-maxhp"
                type="number"
                min={1}
                max={999}
                value={maxHp}
                onChange={(e) => setMaxHp(e.target.value)}
                style={fieldStyle(theme)}
              />
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            marginTop: 14,
            padding: '8px 12px',
            background: 'rgba(212,106,90,0.15)',
            border: `1px solid ${c.danger || '#d46a5a'}`,
            color: c.danger || '#d46a5a',
            borderRadius: 4,
            fontSize: '0.86rem',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '9px 16px',
              background: 'transparent',
              color: c.textSecondary,
              border: `1px solid ${c.borderLight}`,
              borderRadius: 4,
              cursor: busy ? 'wait' : 'pointer',
              fontFamily: t.fontFamily,
              fontSize: '0.88rem',
            }}
          >Cancel</button>
          <button
            type="submit"
            disabled={busy || !canSubmit}
            style={{
              padding: '9px 18px',
              background: c.accent,
              color: c.textOnAccent || '#000',
              border: 'none',
              borderRadius: 4,
              cursor: busy ? 'wait' : 'pointer',
              fontFamily: t.fontFamily,
              fontWeight: 600,
              fontSize: '0.9rem',
              opacity: (busy || !canSubmit) ? 0.7 : 1,
            }}
          >{busy ? 'Creating…' : '✦ Create'}</button>
        </div>

        {onOpenImport && (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              margin: '20px 0 14px',
              color: c.textMuted, fontSize: '0.75rem',
              fontFamily: t.fontFamily,
              letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>
              <span style={{ flex: 1, height: 1, background: c.borderLight }} />
              or
              <span style={{ flex: 1, height: 1, background: c.borderLight }} />
            </div>
            <button
              type="button"
              onClick={() => { if (!busy) onOpenImport(); }}
              disabled={busy}
              style={{
                width: '100%',
                padding: '11px 16px',
                background: 'rgba(0,0,0,0.25)',
                color: c.textPrimary,
                border: `1.5px dashed ${c.borderPrimary}`,
                borderRadius: theme.effects?.borderRadius || '8px',
                cursor: busy ? 'wait' : 'pointer',
                fontFamily: t.fontFamily,
                fontSize: '0.9rem',
              }}
            >
              📄 Import a character sheet
              <span style={{
                display: 'block',
                marginTop: 3,
                color: c.textMuted,
                fontSize: '0.74rem',
                fontStyle: 'italic',
              }}>DDB export or DiceCloud print PDF — reviewed before anything is created</span>
            </button>
          </>
        )}
      </form>
    </div>
  );
};
